import { useSnackbarStore } from '@components/shared/Snackbar/store';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Role, switchRoleService } from '../services/auth/authService';
import { useAuthStore } from '../services/auth/authStore';

// Type for JWT token payload from guards.tsx
interface DecodedToken {
	aud: string;
	email: string;
	exp: number;
	iat: number;
	iss: string;
	role: string;
	sub: string;
	isCompany: boolean;
}

export function useRoleSwitch() {
	const { setAccessToken } = useAuthStore();
	const { t } = useTranslation();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Switch the user's role and refresh the page
	 * This function will call the API to switch roles, save the new tokens,
	 * and then reload the page to let the existing API calls fetch fresh user data
	 */
	const switchRole = async (targetRole: Role) => {
		if (targetRole !== 'farmer' && targetRole !== 'specialist') {
			setError('Invalid role specified');
			showSnackbar(t('error'), t('role_switch.invalid_role'), 'error');
			return { success: false, error: 'Invalid role specified' };
		}

		setIsLoading(true);
		setError(null);

		try {
			const response = await switchRoleService({ role: targetRole });

			if (response.success) {
				const { accessToken, refreshToken } = response.data;
				// Decode the new token to verify the role was updated
				try {
					const decoded = jwtDecode<DecodedToken>(accessToken);

					if (decoded.role.toLowerCase() !== targetRole.toLowerCase()) {
						console.warn(`Role mismatch: token has ${decoded.role}, requested ${targetRole}`);
					}

					// Get the actual role from the token
					const tokenRole = decoded.role.toLowerCase() as Role;

					// Update tokens in auth store and cookies
					setAccessToken(accessToken, refreshToken);

					// Explicitly set the cookie since that's what the RoleGuard checks
					Cookies.set('accessToken', accessToken, { secure: true });
					localStorage.setItem('refreshToken', refreshToken);

					// TODO: Remove this when backend is ready
					Cookies.set('can_switch_role', 'true', { secure: true, expires: 1 });

					// Get current language for navigation
					const currentLanguage = localStorage.getItem('i18nextLng') || 'en';

					let targetPath = '';
					if (tokenRole === 'specialist') {
						targetPath = `/${currentLanguage}/specialist-switcher`;
					} else {
						targetPath = `/${currentLanguage}/${tokenRole}/dashboard`;
					}

					// Show success message and refresh the page after a small delay
					showSnackbar(t('success'), t(`role_switch.success_${tokenRole}`), 'success');

					// Use a short delay to ensure the snackbar is shown before refresh
					setTimeout(() => {
						window.location.href = targetPath;
					}, 300);

					return { success: true, error: null };
				} catch (tokenError) {
					console.error('Error decoding token:', tokenError);

					// If we can't decode the token, just use the requested role
					// Update tokens in auth store and cookies
					setAccessToken(accessToken, refreshToken);
					Cookies.set('accessToken', accessToken, { secure: true });
					localStorage.setItem('refreshToken', refreshToken);

					// Get current language for navigation
					const currentLanguage = localStorage.getItem('i18nextLng') || 'en';
					const targetPath = `/${currentLanguage}/${targetRole}/dashboard`;

					// Show success message and refresh the page after a small delay
					showSnackbar(t('success'), t(`role_switch.success_${targetRole}`), 'success');

					// Use a short delay to ensure the snackbar is shown before refresh
					setTimeout(() => {
						window.location.href = targetPath;
					}, 300);

					return { success: true, error: null };
				}
			} else {
				console.error('API returned error:', response.error);
				setError(response.error || 'Failed to switch role');
				showSnackbar(t('error'), t(response.error || 'role_switch.error'), 'error');
				return { success: false, error: response.error };
			}
		} catch (err: any) {
			console.error('Exception in switchRole:', err);
			const errorMessage = err.response?.data?.error || 'Failed to switch role';
			setError(errorMessage);
			showSnackbar(t('error'), t(errorMessage || 'role_switch.error'), 'error');
			return { success: false, error: errorMessage };
		} finally {
			setIsLoading(false);
		}
	};

	return {
		switchRole,
		isLoading,
		error,
	};
}
